import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";

type ScoreRow = { id: number; score: number; total: number; avg_ms: number | null; created_at: string };

export default function Account() {
  const { user, login, register, logout, loginWithMusic } = useAuth();
  const [mode, setMode] = useState<"login" | "register">("login");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [scores, setScores] = useState<ScoreRow[]>([]);

  useEffect(() => {
    if (!user) {
      setScores([]);
      return;
    }
    void api<{ scores: ScoreRow[] }>("/api/auth/history")
      .then((r) => setScores(r.scores))
      .catch(() => setScores([]));
  }, [user]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      if (mode === "login") await login(username, password);
      else await register(username, email, password);
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  if (user) {
    return (
      <section className="pt-8 grid gap-6 max-w-lg">
        <h1 className="font-display text-4xl">Compte</h1>
        <p>
          Connecté en tant que <strong>{user.username}</strong> — même compte que MusicGuess.
        </p>
        <button
          type="button"
          className="rounded-2xl border border-cream/25 py-3 px-4 w-fit"
          onClick={() => void logout()}
        >
          Déconnexion
        </button>
        <h2 className="font-display text-2xl mt-4">Parties solo</h2>
        {scores.length === 0 ? (
          <p className="opacity-70">Pas encore de score enregistré sur ce compte.</p>
        ) : (
          <ul className="grid gap-2">
            {scores.map((s) => (
              <li key={s.id} className="rounded-2xl bg-ink-2 px-4 py-3 flex justify-between gap-3">
                <span>
                  {s.score}/{s.total}
                </span>
                <span className="opacity-70 text-sm">{s.created_at.replace("T", " ").slice(0, 16)}</span>
              </li>
            ))}
          </ul>
        )}
        <Link to="/solo" className="text-gold">
          Jouer en solo
        </Link>
      </section>
    );
  }

  return (
    <section className="pt-8 grid gap-5 max-w-md">
      <h1 className="font-display text-4xl">{mode === "login" ? "Connexion" : "Créer un compte"}</h1>
      <p className="text-cream/80">
        Les comptes sont partagés avec{" "}
        <a className="text-gold underline" href="https://music.goodloss.fr" target="_blank" rel="noreferrer">
          MusicGuess
        </a>
        . Tu peux jouer sans compte.
      </p>
      <form onSubmit={(e) => void submit(e)} className="grid gap-3">
        <input
          className="rounded-2xl bg-cream text-ink px-4 py-3"
          placeholder="Pseudo"
          autoComplete="username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          maxLength={30}
          required
        />
        {mode === "register" && (
          <input
            className="rounded-2xl bg-cream text-ink px-4 py-3"
            placeholder="E-mail"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        )}
        <input
          className="rounded-2xl bg-cream text-ink px-4 py-3"
          placeholder="Mot de passe"
          type="password"
          autoComplete={mode === "login" ? "current-password" : "new-password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          minLength={4}
          required
        />
        {err && <p className="text-pink">{err}</p>}
        <button disabled={busy} className="rounded-3xl bg-gold text-ink py-4 font-bold" type="submit">
          {mode === "login" ? "Se connecter" : "Créer le compte"}
        </button>
      </form>
      <button type="button" className="rounded-3xl border border-cream/25 py-4" onClick={() => void loginWithMusic()}>
        Continuer avec MusicGuess
      </button>
      <button
        type="button"
        className="text-sm text-gold text-left"
        onClick={() => {
          setMode(mode === "login" ? "register" : "login");
          setErr("");
        }}
      >
        {mode === "login" ? "Pas encore de compte ? Inscription" : "Déjà un compte ? Connexion"}
      </button>
    </section>
  );
}
